import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export const InvoiceMatcherTab = ({
  matchTarget, setMatchTarget, matchStartDate, setMatchStartDate,
  matchEndDate, setMatchEndDate, matchMode, setMatchMode,
  matchResults, matchLoading, handleMatchInvoices,
}) => {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-indigo-700 to-indigo-600 text-white p-4 rounded">
        <div className="flex items-center gap-2">
          <Search className="h-6 w-6" />
          <h2 className="text-2xl font-bold">INVOICE MATCHER</h2>
        </div>
        <p className="text-sm opacity-80 mt-1">Find which invoices add up to a specific payment amount</p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Target Amount (R)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g. 15000.00"
                value={matchTarget}
                onChange={(e) => setMatchTarget(e.target.value)}
                className="mt-1"
                data-testid="match-target-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">From Date</label>
              <Input
                type="date"
                value={matchStartDate}
                onChange={(e) => setMatchStartDate(e.target.value)}
                className="mt-1"
                data-testid="match-start-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">To Date</label>
              <Input
                type="date"
                value={matchEndDate}
                onChange={(e) => setMatchEndDate(e.target.value)}
                className="mt-1"
                data-testid="match-end-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Match By</label>
              <select
                value={matchMode}
                onChange={(e) => setMatchMode(e.target.value)}
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="match-mode-select"
              >
                <option value="outstanding">Outstanding Amount</option>
                <option value="total">Invoice Total</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleMatchInvoices}
                disabled={matchLoading || !matchTarget}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="match-search-btn"
              >
                {matchLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching...</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> Find Match</>
                )}
              </Button>
            </div>
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
            <strong>Outstanding Amount</strong> = Invoice total minus any payments already allocated to it. Use this when matching EFT/bank payments.
            <br /><strong>Invoice Total</strong> = Full original invoice amount. Use this when looking for invoices by their face value.
          </div>

          {matchResults && (
            <div className="space-y-3 pt-2 border-t">
              <div className="text-sm text-gray-600">
                Checked <strong>{matchResults.invoices_checked}</strong> invoices (by {matchResults.match_mode === 'outstanding' ? 'outstanding amount' : 'invoice total'}) for target <strong>R {matchResults.target_amount.toFixed(2)}</strong>
                {matchResults.matches_found > 0
                  ? ` — found ${matchResults.matches_found} combination(s)`
                  : " — no exact match found"}
              </div>

              {matchResults.matches.map((match, mi) => (
                <Card key={mi} className="border-indigo-200 bg-indigo-50/30">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-indigo-700">
                        Match {mi + 1}: {match.count} invoice{match.count > 1 ? "s" : ""}
                      </span>
                      <span className="text-sm font-bold text-green-700">
                        Total: R {match.total.toFixed(2)}
                      </span>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Invoice Total</TableHead>
                          {matchResults.match_mode === 'outstanding' && (
                            <TableHead className="text-right">Outstanding</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {match.invoices.map((inv, ii) => (
                          <TableRow key={ii}>
                            <TableCell className="font-mono font-semibold">{inv.invoice_number}</TableCell>
                            <TableCell>{inv.date}</TableCell>
                            <TableCell className="text-right">R {inv.total.toFixed(2)}</TableCell>
                            {matchResults.match_mode === 'outstanding' && (
                              <TableCell className="text-right font-semibold text-indigo-700">
                                R {inv.outstanding.toFixed(2)}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}

              {/* Close single-invoice suggestions — shown when the user wants to spot
                  an invoice that's roughly the same as the target amount, even
                  though it doesn't add up exactly. */}
              {matchResults.close_singles && matchResults.close_singles.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/40" data-testid="close-singles-card">
                  <CardContent className="p-3">
                    <div className="text-sm font-semibold text-amber-800 mb-2">
                      Closest single invoices to R {matchResults.target_amount.toFixed(2)}
                      <span className="text-xs font-normal text-amber-700 ml-2">
                        (within ±10% or R 500)
                      </span>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Invoice Total</TableHead>
                          {matchResults.match_mode === 'outstanding' && (
                            <TableHead className="text-right">Outstanding</TableHead>
                          )}
                          <TableHead className="text-right">Difference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {matchResults.close_singles.map((inv, ii) => (
                          <TableRow key={ii} data-testid={`close-single-row-${ii}`}>
                            <TableCell className="font-mono font-semibold">{inv.invoice_number}</TableCell>
                            <TableCell>{inv.date}</TableCell>
                            <TableCell className="text-right">R {inv.total.toFixed(2)}</TableCell>
                            {matchResults.match_mode === 'outstanding' && (
                              <TableCell className="text-right text-indigo-700">
                                R {inv.outstanding.toFixed(2)}
                              </TableCell>
                            )}
                            <TableCell className={`text-right font-semibold ${inv.diff > 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                              {inv.diff > 0 ? '+' : ''}R {inv.diff.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Close combinations — multi-invoice combinations whose total is
                  roughly the target amount but not exact. Useful when a customer's
                  EFT amount doesn't match any subset exactly. */}
              {matchResults.close_combinations && matchResults.close_combinations.length > 0 && (
                <Card className="border-purple-200 bg-purple-50/40" data-testid="close-combinations-card">
                  <CardContent className="p-3">
                    <div className="text-sm font-semibold text-purple-800 mb-2">
                      Closest invoice combinations to R {matchResults.target_amount.toFixed(2)}
                      <span className="text-xs font-normal text-purple-700 ml-2">
                        (within ±R {matchResults.close_band?.toFixed(2)})
                      </span>
                    </div>
                    {matchResults.close_combinations.map((combo, ci) => (
                      <div key={ci} className="mb-3 last:mb-0 border rounded bg-white p-2" data-testid={`close-combo-${ci}`}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-semibold text-purple-700">
                            Combo {ci + 1}: {combo.count} invoice{combo.count > 1 ? "s" : ""}
                          </span>
                          <span>
                            Total <strong>R {combo.total.toFixed(2)}</strong>
                            <span className={`ml-2 font-semibold ${combo.diff > 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                              ({combo.diff > 0 ? '+' : ''}R {combo.diff.toFixed(2)})
                            </span>
                          </span>
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="h-7">Invoice</TableHead>
                              <TableHead className="h-7">Date</TableHead>
                              <TableHead className="text-right h-7">Total</TableHead>
                              {matchResults.match_mode === 'outstanding' && (
                                <TableHead className="text-right h-7">Outstanding</TableHead>
                              )}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {combo.invoices.map((inv, ii) => (
                              <TableRow key={ii}>
                                <TableCell className="font-mono py-1">{inv.invoice_number}</TableCell>
                                <TableCell className="py-1">{inv.date}</TableCell>
                                <TableCell className="text-right py-1">R {inv.total.toFixed(2)}</TableCell>
                                {matchResults.match_mode === 'outstanding' && (
                                  <TableCell className="text-right py-1 text-indigo-700">
                                    R {inv.outstanding.toFixed(2)}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
